import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SubcollectionHeader } from 'src/components/Collection/SubcollectionHeader'
import { CollectionHeadline } from 'src/components/CollectionHeadline/CollectionHeadline'
import { ProductGrid } from 'src/components/ProductGrid/ProductGrid'
import { useLocale } from 'src/contexts/LocaleContext'
import { getFilteredProducts, getFilters } from 'src/functions/product/productFilters'
import { getMetafield } from 'src/functions/util/getMetafield'

import type { Collection } from 'src/data/graphql-generated'
import type { CollectionBreadcrumb } from 'src/types/Collection'
import type { ProductRating } from 'src/types/Product'

interface CollectionPageProps {
  collection: Collection
  breadcrumbs: CollectionBreadcrumb[]
  ratings: Record<string, ProductRating>
  gearSubcollections: Collection[]
  coffeeSubcollections: Collection[]
}

export const CollectionPage = ({
  collection,
  breadcrumbs = [],
  ratings = {},
  gearSubcollections = [],
  coffeeSubcollections = [],
}: CollectionPageProps) => {
  const { locale } = useLocale()
  const { t } = useTranslation()

  const [activeFilter, setActiveFilter] = useState<string>('')
  const filters = getFilters(collection, locale)
  const filteredProducts = getFilteredProducts(collection, activeFilter, locale)

  const { metafields } = collection

  const showVariants = !gearSubcollections.length && Boolean(getMetafield(metafields, 'layout', 'display_variants'))
  const largeCardProductIds: string[] = getMetafield(metafields, 'layout', 'large_card_products') || []

  return (
    <>
      <CollectionHeadline
        collection={collection}
        breadcrumbs={breadcrumbs}
        coffeeSubcollections={coffeeSubcollections}
        filtersMeta={{ filters, activeFilter, setActiveFilter }}
      />

      {!gearSubcollections.length ? (
        <>
          <h2 className="visually-hidden">{t('common.products')}</h2>
          <ProductGrid
            products={filteredProducts}
            largeCardProductIds={largeCardProductIds}
            showVariants={showVariants}
            ratings={ratings}
            preload
          />
        </>
      ) : (
        gearSubcollections.map((subcollection, i) => (
          <div key={`subcollection-${subcollection.id}`}>
            <SubcollectionHeader subcollection={subcollection} />
            <ProductGrid
              products={subcollection.products.nodes || []}
              showVariants={showVariants}
              ratings={ratings}
              preload={i === 0}
              preventLarge
            />
          </div>
        ))
      )}
    </>
  )
}
